import React, { useCallback, useMemo } from 'react';
import { ATGL, COMPONENT_RENDERER_TYPE } from '@uber/atgl-components';
import CircleI from '@uber/base-icons/circle-i';
import PaperclipFilled from '@uber/base-icons/paperclip-filled';
import XOutlined from '@uber/base-icons/x-outlined';
import { styled, useStyletron } from 'baseui';
import { Banner, KIND as BANNER_KIND } from 'baseui/banner';
import { Modal, ModalBody, ModalHeader, ROLE, SIZE } from 'baseui/modal';
import { PLACEMENT, StatefulPopover, TRIGGER_TYPE } from 'baseui/popover';
import { Spinner } from 'baseui/spinner';
import { KIND as TAG_KIND, SIZE as TAG_SIZE, Tag } from 'baseui/tag';
import { LabelMedium } from 'baseui/typography';
import { useCachedFileUpload } from '../hooks/use-cached-file-upload';
import type { GenAIValidationProps, GeneratedContent, UploadedFile } from '../types/types';
import { sanitizeIdFromATGLConfig } from '../utils/atgl-helper';
import { getMimeType, removeMediaFile } from '../utils/media-file-utils';
import { GeneratedContentDisplay } from './generated-content-display';
import type { MediaFile } from './media-input';
import { MediaInput } from './media-input';
import { Search } from './search';
const Container = styled('div', ({
  $theme
}) => ({
  padding: `${$theme.sizing.scale400}`
}));
const LoadingContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `${$theme.sizing.scale800}`
}));
const ErrorContainer = styled('div', ({
  $theme
}) => ({
  padding: `${$theme.sizing.scale400} 0`,
  color: $theme.colors.contentNegative
}));
const ContextContainer = styled('div', ({
  $theme
}) => ({
  padding: `${$theme.sizing.scale400} 0`,
  marginBottom: $theme.sizing.scale400
}));
const SearchRow = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale300,
  alignItems: 'center',
  width: '100%'
}));
const MediaWrapper = styled('div', ({
  $theme
}) => ({
  marginTop: $theme.sizing.scale400,
  marginLeft: 0,
  paddingLeft: 0
}));
const MediaPreviewContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: $theme.sizing.scale100,
  alignItems: 'flex-start'
}));
const FileNameContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  maxWidth: '200px'
});
const PopoverContent = styled('div', ({
  $theme
}) => ({
  ...$theme.typography.LabelSmall,
  padding: $theme.sizing.scale300,
  paddingTop: $theme.sizing.scale200,
  maxWidth: '400px',
  wordWrap: 'break-word',
  whiteSpace: 'normal'
}));
const TagWrapper = styled('div', {
  display: 'inline-block'
});
const FileNameText = styled('span', ({
  $theme
}) => ({
  marginLeft: $theme.sizing.scale200,
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));
const RemoveButton = styled('button', ({
  $theme
}) => ({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  marginLeft: $theme.sizing.scale200,
  display: 'flex',
  alignItems: 'center',
  color: 'inherit',
  flexShrink: 0,
  ':hover': {
    opacity: 0.7
  },
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.5
  }
}));
const ATGLContentContainer = styled('div', ({
  $theme
}) => ({
  marginTop: $theme.sizing.scale600,
  marginBottom: $theme.sizing.scale600,
  padding: $theme.sizing.scale600,
  backgroundColor: $theme.colors.backgroundSecondary,
  borderRadius: $theme.borders.radius300,
  border: `1px solid ${$theme.colors.borderOpaque}`
}));
const ATGLHeaderContainer = styled('div', ({
  $theme
}) => ({
  borderBottom: `1px solid ${$theme.colors.borderOpaque}`,
  marginBottom: $theme.sizing.scale200
}));
const ATGLContentWrapper = styled('div', {
  minHeight: '200px',
  width: '100%'
});
const ModalPreviewContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  minHeight: '200px',
  padding: $theme.sizing.scale400,
  overflow: 'auto'
}));
const ModalImage = styled('img', {
  maxWidth: '100%',
  maxHeight: 'calc(90vh - 120px)',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain'
});
const ModalVideo = styled('video', {
  maxWidth: '100%',
  maxHeight: 'calc(90vh - 120px)',
  width: 'auto',
  height: 'auto'
});
const ModalPdfFrame = styled('iframe', {
  width: '100%',
  minHeight: 'calc(90vh - 120px)',
  border: 'none'
});
const GenerationRemainingBanner = styled('div', ({
  $theme
}) => ({
  marginBottom: $theme.sizing.scale400
}));
const ARTWORK = {
  icon: () => <CircleI size={20} />
};
const OVERRIDES = {
  Root: {
    style: {
      marginLeft: 0,
      marginRight: 0,
      marginBottom: 0
    }
  },
  Message: {
    style: ({
      $theme
    }) => ({
      ...$theme.typography.ParagraphSmall
    })
  }
};
const ATGL_RESULT_LABEL = 'Input Media';
const PROMPT_KEY = 'text_primary_1';
const TAG_OVERRIDES = {
  Root: {
    style: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap'
    }
  },
  Text: {
    style: {
      display: 'flex',
      alignItems: 'center',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: '1 1 auto',
      minWidth: 0
    }
  }
};
const fileIconStyles = ({
  flexShrink: 0
} as const);
export const GenAIValidation = ({
  hideInput = false,
  noOfColumnsPerRow = 1,
  componentConfigMap,
  inputData,
  preParseFnList: _preParseFnList,
  onStateChange,
  aiGateway,
  generationConfig,
  logger,
  showNotification,
  resetInputSearch,
  hideClearBtn,
  enableMediaInput = false,
  maxMediaFiles = 5,
  generationRemaining,
  uploadMediaFiles,
  customComponents,
  initialSearchTerm,
  initialGeneratedContent
}: GenAIValidationProps) => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = React.useState<GeneratedContent[] | null>(initialGeneratedContent ?? null);
  const [mediaFiles, setMediaFiles] = React.useState<MediaFile[]>([]);
  const [previewFile, setPreviewFile] = React.useState<MediaFile | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const {
    uploadFiles,
    getUploadedFile
  } = useCachedFileUpload({
    uploadMediaFiles,
    logger
  });
  const [css] = useStyletron();
  const contextMessage = typeof inputData?.[PROMPT_KEY] === 'object' && inputData?.[PROMPT_KEY] !== null && 'content' in inputData[PROMPT_KEY] ? inputData.text_primary_1.content : undefined;
  const handleMediaChange = useCallback((files: MediaFile[]) => {
    setMediaFiles(files);
  }, []);
  const handleRemoveMedia = useCallback((removedIdx: number) => {
    const updatedFiles = removeMediaFile(mediaFiles, removedIdx);
    setMediaFiles(updatedFiles);
  }, [mediaFiles]);
  const handleOpenPreview = useCallback((file: MediaFile) => {
    setPreviewFile(file);
  }, []);
  const handleClosePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);
  const inputMediaAtglConfig = useMemo(() => {
    if (!inputData || !componentConfigMap) {
      return null;
    }
    return {
      ...sanitizeIdFromATGLConfig(inputData, componentConfigMap, [PROMPT_KEY]),
      rootKey: 'ROOT'
    };
  }, [inputData, componentConfigMap]);
  const handleSearch = async (searchTerm: string) => {
    if (searchTerm === '') {
      if (resetInputSearch) {
        resetInputSearch();
      }
      setGeneratedContent([]);
      return;
    }
    if (onStateChange) {
      const labellingData = {
        labellingType: ('GEN_AI_LABELING' as const),
        searchTerm
      };
      onStateChange(labellingData);
    }
    if (!aiGateway || !generationConfig) {
      if (logger) {
        logger.error('Missing required parameters for AI Gateway', {
          hasAiGateway: !!aiGateway,
          hasGenerationConfig: !!generationConfig
        });
      }
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      let uploadedFiles: UploadedFile[] = [];
      if (mediaFiles.length > 0) {
        const uploadResult = await uploadFiles(mediaFiles);
        if (uploadResult instanceof Error) {
          throw uploadResult;
        }
        uploadedFiles = uploadResult;
      }
      let ingestedFiles: {
        key: string;
        mimeType: string;
      }[] = [];
      if (inputMediaAtglConfig?.inputData) {
        ingestedFiles = Object.entries(inputMediaAtglConfig.componentConfigMap).filter(([_key, value]: [string, {
          type: COMPONENT_RENDERER_TYPE;
        }]) => value.type === COMPONENT_RENDERER_TYPE.IMAGE || value.type === COMPONENT_RENDERER_TYPE.VIDEO || value.type === COMPONENT_RENDERER_TYPE.AUDIO || value.type === COMPONENT_RENDERER_TYPE.PDF).map(([key, value]: [string, {
          type: COMPONENT_RENDERER_TYPE;
        }]) => ({
          key,
          mimeType: getMimeType(value.type) || ''
        }));
      }
      const startResult = await aiGateway.startContentGeneration(searchTerm, generationConfig, uploadedFiles, ingestedFiles);
      if (startResult instanceof Error) {
        throw startResult;
      }
      const contentResult = await aiGateway.getGeneratedContent(startResult.operationId);
      if (contentResult instanceof Error) {
        throw contentResult;
      }
      if (!contentResult) {
        throw new Error('No content generated');
      }
      setGeneratedContent(contentResult);
      if (onStateChange) {
        const labellingData = {
          labellingType: ('GEN_AI_LABELING' as const),
          searchTerm,
          generatedContent: contentResult,
          operationId: startResult.operationId,
          promptAttachmentsUri: mediaFiles.map(({
            file
          }) => getUploadedFile(file)?.uploadPath)
        };
        onStateChange(labellingData);
      }
      if (showNotification) {
        showNotification('Content generated successfully!', 'info');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate content';
      setError(errorMessage);
      if (logger) {
        logger.error('GenAI content generation failed', err);
      }
      if (showNotification) {
        showNotification(errorMessage, 'error');
      }
    } finally {
      setIsGenerating(false);
    }
  };
  const shouldShowMediaInputAtgl = inputMediaAtglConfig && Object.keys(inputMediaAtglConfig.inputData).length > 0;
  const {
    preview: previewUrl
  } = previewFile || {};
  const isGenerationLimitReached = generationRemaining !== undefined && generationRemaining <= 0;
  return <Container>
      {contextMessage && <ContextContainer>
          <LabelMedium>{contextMessage}</LabelMedium>
        </ContextContainer>}

      <SearchRow>
        <Search onSearch={handleSearch} placeholder="Enter prompt..." disabled={isGenerating} initialValue={initialSearchTerm} bottomEnhancer={enableMediaInput ? <MediaInput onMediaChange={handleMediaChange} disabled={isGenerating} maxFiles={maxMediaFiles} value={mediaFiles} /> : null} />
      </SearchRow>

      {generationRemaining !== undefined && <GenerationRemainingBanner>
          <Banner kind={isGenerationLimitReached ? BANNER_KIND.negative : BANNER_KIND.info} artwork={ARTWORK} overrides={OVERRIDES}>
            {isGenerationLimitReached ? `You have reached the maximum number of generations allowed for this task.` : `You have ${generationRemaining} generation${generationRemaining > 1 ? 's' : ''} remaining for this task.`}
          </Banner>
        </GenerationRemainingBanner>}
      {mediaFiles.length > 0 ? <MediaWrapper>
          <MediaPreviewContainer>
            {mediaFiles.map((mediaFile, index) => <FileNameContainer key={index}>
                <StatefulPopover content={<PopoverContent>{mediaFile.file.name}</PopoverContent>} triggerType={TRIGGER_TYPE.hover} placement={PLACEMENT.bottom} popoverMargin={8} accessibilityType="tooltip" showArrow overrides={{
            Inner: {
              style: ({
                $theme
              }) => ({
                paddingTop: 0,
                backgroundColor: $theme.colors.backgroundPrimary
              })
            },
            Arrow: {
              style: ({
                $theme
              }) => ({
                top: 0,
                backgroundColor: $theme.colors.backgroundPrimary
              })
            }
          }}>
                  <TagWrapper>
                    <Tag closeable={false} kind={TAG_KIND.primary} size={TAG_SIZE.medium} overrides={{
                ...TAG_OVERRIDES,
                Root: {
                  style: {
                    ...TAG_OVERRIDES.Root.style,
                    cursor: 'pointer'
                  }
                }
              }} onClick={() => handleOpenPreview(mediaFile)}>
                      <PaperclipFilled size={12} title="" className={css(fileIconStyles)} />
                      <FileNameText>{mediaFile.file.name}</FileNameText>
                      <RemoveButton onClick={e => {
                  e.stopPropagation();
                  handleRemoveMedia(index);
                }} onMouseDown={e => {
                  e.stopPropagation();
                }} disabled={isGenerating || previewFile === mediaFile} aria-label="Remove media">
                        <XOutlined size={12} />
                      </RemoveButton>
                    </Tag>
                  </TagWrapper>
                </StatefulPopover>
              </FileNameContainer>)}
          </MediaPreviewContainer>
        </MediaWrapper> : null}

      {shouldShowMediaInputAtgl && inputMediaAtglConfig && !hideInput ? <ATGLContentContainer>
          <ATGLHeaderContainer>
            <LabelMedium>{ATGL_RESULT_LABEL}</LabelMedium>
          </ATGLHeaderContainer>
          <ATGLContentWrapper>
            <ATGL {...inputMediaAtglConfig} customComponents={customComponents} />
          </ATGLContentWrapper>
        </ATGLContentContainer> : null}

      {error && <ErrorContainer>
          <LabelMedium>Error: {error}</LabelMedium>
        </ErrorContainer>}

      {isGenerating && <LoadingContainer>
          <Spinner $size="medium" />
          <LabelMedium style={{
        marginLeft: '8px'
      }}>Generating content...</LabelMedium>
        </LoadingContainer>}

      {generatedContent && !isGenerating && <GeneratedContentDisplay generatedContent={generatedContent} noOfColumnsPerRow={noOfColumnsPerRow} />}

      {previewFile && <Modal key={previewFile.preview} isOpen={!!previewFile} onClose={handleClosePreview} closeable animate autoFocus size={SIZE.auto} role={ROLE.dialog} overrides={{
      Dialog: {
        style: {
          width: '90vw',
          maxWidth: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      },
      DialogContainer: {
        style: {
          alignItems: 'center',
          justifyContent: 'center'
        }
      }
    }}>
          <ModalHeader>{previewFile.file.name}</ModalHeader>
          <ModalBody>
            <ModalPreviewContainer>
              {previewFile.type === 'image' && previewUrl && <ModalImage src={previewUrl} alt={previewFile.file.name} />}
              {previewFile.type === 'video' && previewUrl && <ModalVideo ref={videoRef} src={previewUrl} controls preload="auto" playsInline key={previewUrl} autoPlay muted>
                  <source src={previewUrl} type={previewFile.file.type || 'video/mp4'} />
                  Your browser does not support the video tag.
                </ModalVideo>}
              {previewFile.type === 'pdf' && previewUrl && <ModalPdfFrame src={previewUrl} title={previewFile.file.name} />}
            </ModalPreviewContainer>
          </ModalBody>
        </Modal>}
    </Container>;
};